/**
 * Python Installation Helper Component
 * Guides users through checking and installing Python
 */

import {
  getPythonDownloadUrl,
  getPythonInstallTips,
  getPythonCheckCommand,
  getPythonErrorSolutions,
  type Platform,
} from "@utils/platform";
import { CodeBlock } from "@components/common";
import { ExpandableSection } from "./ExpandableSection";

interface PythonInstallHelperProps {
  platform: Platform;
}

export function PythonInstallHelper({ platform }: PythonInstallHelperProps) {
  const downloadUrl = getPythonDownloadUrl(platform);
  const installTips = getPythonInstallTips(platform);
  const checkCommand = getPythonCheckCommand(platform);
  const errorSolutions = getPythonErrorSolutions(platform);

  return (
    <div className="space-y-3">
      {/* Not sure if Python is installed */}
      <ExpandableSection
        title="Not sure if you have Python installed?"
        variant="help"
        icon={
          <svg
            className="w-4 h-4 text-blue-600 dark:text-blue-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Let's check! Open your terminal and run this command:
          </p>
          <CodeBlock code={checkCommand} title="Check Python version" />

          <div className="space-y-3 mt-4">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    If you see "Python 3.10.x" or higher
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                    Great! Python is installed. You can continue to the next
                    step.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    If you see an error or older version
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    You'll need to install Python. Expand the section below for
                    instructions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ExpandableSection>

      {/* Need to install Python */}
      <ExpandableSection
        title="Need to install Python?"
        variant="warning"
        icon={
          <svg
            className="w-4 h-4 text-amber-600 dark:text-amber-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        }
      >
        <div className="space-y-4">
          {/* Download link */}
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Download Python from the official website:
            </p>
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors text-sm font-medium"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download Python for{" "}
              {platform === "macos"
                ? "macOS"
                : platform === "windows"
                  ? "Windows"
                  : "Linux"}
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          </div>

          {/* Installation steps */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Installation steps:
            </h4>
            <ol className="space-y-2">
              {installTips.tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-full flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </span>
                  <span
                    className={`text-sm pt-0.5 ${
                      tip.includes("IMPORTANT")
                        ? "text-amber-700 dark:text-amber-300 font-medium"
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {tip}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* Windows PATH warning */}
          {installTips.warning && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    Important for Windows users!
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                    {installTips.warning}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Package manager alternative */}
          {installTips.packageManager && (
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Alternative method:
              </p>
              <code className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {installTips.packageManager}
              </code>
            </div>
          )}
        </div>
      </ExpandableSection>

      {/* Troubleshooting */}
      <ExpandableSection
        title="Seeing an error? Common solutions"
        variant="info"
        icon={
          <svg
            className="w-4 h-4 text-purple-600 dark:text-purple-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
      >
        <div className="space-y-3">
          {errorSolutions.map((item, index) => (
            <div
              key={index}
              className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-start gap-2 mb-2">
                <code className="text-xs font-mono bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-2 py-1 rounded">
                  {item.error}
                </code>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium text-gray-900 dark:text-white">
                  Solution:{" "}
                </span>
                {item.solution}
              </p>
            </div>
          ))}
        </div>
      </ExpandableSection>
    </div>
  );
}
